import { Button, Tooltip, notification } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import TruncatedTooltip from '../components/Common/TruncatedTooltip';
import { Icon } from '../components/Core/Icon';
import useProtectedFile from '../hooks/useProtectedFile';
import { dateFromTicks } from '../services/utils';
import { copyToClipboard } from '../utils/utils';

export default function ProtectedFile() {
  const {
    file,
    isLoading,
    otp,
    handleDownloadFile,
    isDownloadHelpTextVisible,
    isWindowsPlatform,
    formatTime,
    timeLeft,
    getFileOtp,
    isOtpExpired,
  } = useProtectedFile();
  const { t } = useTranslation();

  return (
    <div className="h-full w-full overflow-x-hidden rounded-md border border-gray-medium bg-white">
      <div className="flex w-full flex-col items-center justify-center gap-4 truncate p-8 text-center">
        <span
          className={clsx(
            'text-dark-blue w-full px-2 text-2xl font-bold transition-all duration-200 md:text-5xl',
            isLoading && 'blur-md',
          )}
        >
          <TruncatedTooltip className="leading-loose">{file?.fileName || 'N/D'}</TruncatedTooltip>
        </span>
        <div className="grid grid-cols-2 items-center gap-2 text-lg">
          <span className="text-right font-medium text-blue-dark">{t('common.author')}:</span>
          <span className="text-left">{file?.createdByName}</span>
          <span className="text-right font-medium text-blue-dark">{t('common.creationDate')}:</span>
          <span className="text-left font-medium">
            {file?.createdAt ? dateFromTicks(file?.createdAt).toLocaleString() : 'N/D'}
          </span>
        </div>
        <Icon name="CameraOffIcon" className="my-4 h-16 w-16 text-blue" />
        <div className="flex w-full flex-col items-center gap-2 rounded-md border border-gray-medium bg-gray-light p-4 text-blue-dark md:flex-row">
          <Icon name="CircleAlertIcon" className="h-10 w-10 text-blue-dark" />
          <span className="text-wrap">{t('notifications.fileProtectedWithAntiCapture')}</span>
        </div>
        <div className="flex flex-col items-center justify-center gap-1">
          <div className="flex flex-row items-center gap-2">
            <span
              className={clsx(
                'font-bold tracking-widest text-blue-dark transition-all duration-200',
                isLoading && 'blur-md',
                isOtpExpired ? 'text-xl' : 'text-4xl',
              )}
            >
              {otp && !isOtpExpired ? otp : '- - - -'}
            </span>
            <Tooltip title={isOtpExpired ? t('common.otpExpired') : t('common.copyToClipboard')}>
              <Button
                type="text"
                size="small"
                disabled={!otp || isOtpExpired}
                loading={isLoading}
                onClick={() => {
                  copyToClipboard(`${otp}`)
                    .then(() => {
                      notification.success({
                        message: t('notifications.otpCopiedToClipboard'),
                      });
                    })
                    .catch(() => {
                      notification.error({
                        message: t('errors.hitASnag'),
                      });
                    });
                }}
              >
                <Icon name="CopyIcon" className="h-4 w-4 text-gray-dark" />
              </Button>
            </Tooltip>
          </div>

          {timeLeft > 0 ? (
            <span className={clsx('text-xs', isLoading && 'blur-md')}>
              {t('common.youCanRequestANewOtpIn')} {formatTime(timeLeft)}
            </span>
          ) : (
            <div
              className={clsx(
                'flex flex-row items-center gap-1 text-xs text-red-dark',
                isLoading && 'blur-md',
              )}
            >
              <Button type="link" className="underline" loading={isLoading} onClick={getFileOtp}>
                {t('common.clickHereToGenerateNewOtp')}
              </Button>
            </div>
          )}
        </div>
        <Button
          type="primary"
          loading={isLoading}
          disabled={!otp || !isWindowsPlatform}
          onClick={handleDownloadFile}
        >
          {t('common.openProtectedFile')}
        </Button>
        {isDownloadHelpTextVisible && (
          <span className="text-wrap text-center font-medium text-green-dark">
            {t('notifications.fileProtectedDownloadInProgress')}
          </span>
        )}
        {!isWindowsPlatform && (
          <span className="text-wrap text-center font-medium text-red-dark">
            {t('errors.protectedFileDownloadOnlyForWindows')}
          </span>
        )}
      </div>
    </div>
  );
}
